import React, { useMemo } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import RegistryFieldPicker, { defaultFetchLocalEntryRegistries, shareRegistryFetch } from './RegistryFieldPicker';
import { validateBindings } from '../utils/registryBindings';
import { MONO_FONT } from '../constants/typography';

const PATH_HINT = 'chiave.sottochiave, [*] = ogni elemento';
const EMPTY_BINDING = { path: '', registryId: '', field: '', required: false };

/**
 * Controlled editor for `schema.registryBindings` of a JSON attribute.
 * Rows: path (monospace) + registry/field picker + "Obbligatorio" + remove.
 * Errors come from validateBindings (grammar §3.1, duplicates, missing target);
 * they are shown once a row is touched, while the parent gates on
 * validateBindings(bindings).valid regardless.
 */
const RegistryBindingsEditor = ({
  bindings = [],
  onChange,
  fetchRegistries = defaultFetchLocalEntryRegistries,
  showHeading = true,
}) => {
  // One registry fetch shared by every row's picker (each picker fetches on mount).
  const sharedFetch = useMemo(() => shareRegistryFetch(fetchRegistries), [fetchRegistries]);

  const { rows: errors } = validateBindings(bindings);

  const update = (index, patch) =>
    onChange(bindings.map((binding, i) => (i === index ? { ...binding, ...patch } : binding)));
  const remove = (index) => onChange(bindings.filter((_, i) => i !== index));
  const add = () => onChange([...bindings, { ...EMPTY_BINDING }]);

  return (
    <Box>
      {showHeading && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Binding anagrafiche
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
            Collega un percorso del JSON a un campo di anagrafica: i valori estratti in quel punto
            saranno verificati contro le sue voci.
          </Typography>
        </>
      )}

      {bindings.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Nessun binding definito.
        </Typography>
      )}

      {bindings.map((binding, index) => {
        const rowErrors = errors[index] || {};
        const touched = Boolean((binding.path || '').trim() || binding.registryId);
        const pathError = touched ? rowErrors.path : null;
        const targetError = touched ? rowErrors.target : null;
        return (
          <Box
            key={index}
            sx={{ py: 1.5, borderTop: index > 0 ? '1px solid' : 'none', borderColor: 'divider' }}
          >
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Percorso"
                  value={binding.path}
                  onChange={(e) => update(index, { path: e.target.value })}
                  error={Boolean(pathError)}
                  helperText={pathError || PATH_HINT}
                  InputProps={{ sx: { fontFamily: MONO_FONT, fontSize: '0.8125rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <RegistryFieldPicker
                  value={{ registryId: binding.registryId, field: binding.field }}
                  onChange={(cfg) => update(index, cfg)}
                  fetchRegistries={sharedFetch}
                  keyFixLabel={`Usa la chiave per il binding «${(binding.path || '').trim() || `n° ${index + 1}`}»`}
                />
                {targetError && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, ml: 1.75 }}>
                    {targetError}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={9} md={2}>
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={Boolean(binding.required)}
                      onChange={(e) => update(index, { required: e.target.checked })}
                    />
                  )}
                  label="Obbligatorio"
                />
              </Grid>
              <Grid item xs={3} md={1} sx={{ textAlign: 'right' }}>
                <Tooltip title="Rimuovi binding">
                  <IconButton aria-label="Rimuovi binding" size="small" onClick={() => remove(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        );
      })}

      <Button size="small" startIcon={<AddIcon />} onClick={add} sx={{ mt: 0.5 }}>
        Aggiungi binding
      </Button>
    </Box>
  );
};

export default RegistryBindingsEditor;
